import { z } from 'zod';

import {
  stationKindValues,
  stationStatusValues,
  type CreateStationRequest,
  type CreateStationResponse,
  type GetStationRequest,
  type GetStationResponse,
  type ListStationsByLocationRequest,
  type ListStationsByLocationResponse,
  type StationDto,
  type StationKind,
  type StationStatus,
  type UpdateStationRequest,
  type UpdateStationResponse,
} from './station.dto.js';

const timestampSchema = z.iso.datetime();

export const StationSchema: z.ZodType<StationDto> = z.object({
  id: z.uuid(),
  companyId: z.uuid(),
  storeId: z.uuid(),
  locationId: z.uuid(),
  name: z.string().trim().min(1),
  kind: z.enum(stationKindValues),
  isOfflineCapable: z.boolean(),
  status: z.enum(stationStatusValues),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});

export const CreateStationRequestSchema: z.ZodType<CreateStationRequest> = z.object({
  companyId: z.uuid(),
  storeId: z.uuid(),
  locationId: z.uuid(),
  name: z.string().trim().min(1),
  kind: z.enum(stationKindValues),
  isOfflineCapable: z.boolean(),
});

export const CreateStationResponseSchema: z.ZodType<CreateStationResponse> = z.object({
  station: StationSchema,
});

export const UpdateStationRequestSchema: z.ZodType<UpdateStationRequest> = z
  .object({
    companyId: z.uuid(),
    storeId: z.uuid(),
    locationId: z.uuid(),
    stationId: z.uuid(),
    name: z.string().trim().min(1).optional(),
    kind: z.enum(stationKindValues).optional(),
    isOfflineCapable: z.boolean().optional(),
    status: z.enum(stationStatusValues).optional(),
  })
  .refine(
    (value) =>
      value.name !== undefined ||
      value.kind !== undefined ||
      value.isOfflineCapable !== undefined ||
      value.status !== undefined,
    { message: 'At least one field must be provided to update station' },
  );

export const UpdateStationResponseSchema: z.ZodType<UpdateStationResponse> = z.object({
  station: StationSchema,
});

export const GetStationRequestSchema: z.ZodType<GetStationRequest> = z.object({
  companyId: z.uuid(),
  stationId: z.uuid(),
});

export const GetStationResponseSchema: z.ZodType<GetStationResponse> = z.object({
  station: StationSchema,
});

export const ListStationsByLocationRequestSchema: z.ZodType<ListStationsByLocationRequest> = z.object({
  companyId: z.uuid(),
  storeId: z.uuid(),
  locationId: z.uuid(),
  page: z.number().int().min(1).optional(),
  pageSize: z.number().int().min(1).max(200).optional(),
});

export const ListStationsByLocationResponseSchema: z.ZodType<ListStationsByLocationResponse> = z.object({
  items: z.array(StationSchema),
  page: z.number().int().min(1),
  pageSize: z.number().int().min(1).max(200),
  total: z.number().int().min(0),
});

export const StationKindSchema: z.ZodType<StationKind> = z.enum(stationKindValues);
export const StationStatusSchema: z.ZodType<StationStatus> = z.enum(stationStatusValues);
